import { computed, defineComponent, h, ref } from 'vue'
import type { DialogServiceMethods } from 'primevue/dialogservice'
import Button from 'primevue/button'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import InputText from 'primevue/inputtext'
import { createDeferred } from '@/utils/deferred'
import SidebarTile from './SidebarTile.vue'
import { NAV_ICON_GROUPS } from './navIcons'
import { NAV_COLORS, type NavColor } from './navColors'
import { MAX_SIDEBAR_TEXT_LENGTH, type SidebarLinkOverrides } from './sidebarLayout'
import type { ResolvedSidebarLink, SidebarLayoutController } from './useSidebarLayout'
import './sidebar-dialogs.css'

/**
 * Lets a person rename a sidebar item and pick its icon and color. Resolves to the chosen values,
 * or null when cancelled. A value equal to the page's default is dropped when stored, so "Reset"
 * simply puts the default back.
 */
export function promptEditSidebarLink (
  dialog: DialogServiceMethods,
  { link }: { link: ResolvedSidebarLink }
): Promise<SidebarLinkOverrides | null> {
  const deferred = createDeferred<SidebarLinkOverrides | null>()
  const defaults = { label: link.entry.label, icon: link.entry.icon, color: link.entry.color }

  function finish (result: SidebarLinkOverrides | null) {
    if (!deferred.isPending) {
      return
    }
    deferred.resolve(result)
    dialogRef.close()
  }

  const DialogContent = defineComponent({
    setup () {
      const label = ref(link.label)
      const icon = ref(link.icon)
      const color = ref<NavColor>(link.color)
      const iconFilter = ref('')

      const previewLabel = computed(() => label.value.trim() || defaults.label)

      const iconGroups = computed(() => {
        const filter = iconFilter.value.trim().toLowerCase()
        return NAV_ICON_GROUPS
          .map(group => ({
            label: group.label,
            icons: Object.entries(group.icons).filter(([name]) => !filter || name.toLowerCase().includes(filter))
          }))
          .filter(group => group.icons.length > 0)
      })

      const resetButton = (visible: boolean, onClick: () => void) => visible && (
        <button type="button" class="sidebar-editor-reset" onClick={onClick}>Reset to default</button>
      )

      const save = () => finish({ label: label.value.trim() || defaults.label, icon: icon.value, color: color.value })

      return () => (
        <div class="sidebar-link-editor">
          <div class="sidebar-editor-preview" aria-hidden="true">
            <SidebarTile icon={icon.value} color={color.value} />
            <span>{previewLabel.value}</span>
          </div>

          <div class="sidebar-editor-field">
            <div class="sidebar-editor-label-row">
              <label for="sidebar-link-name">Name</label>
              {resetButton(label.value.trim() !== defaults.label, () => { label.value = defaults.label })}
            </div>
            <InputText
              id="sidebar-link-name"
              v-model={label.value}
              placeholder={defaults.label}
              maxlength={MAX_SIDEBAR_TEXT_LENGTH}
              size="small"
              class="tw:w-full"
              onKeydown={(event: KeyboardEvent) => { if (event.key === 'Enter') save() }}
            />
            { link.entry.trail !== defaults.label &&
            <small class="sidebar-editor-hint">Opens {link.entry.trail}</small>
            }
          </div>

          <div class="sidebar-editor-field">
            <div class="sidebar-editor-label-row">
              <span class="sidebar-editor-label">Color</span>
              {resetButton(color.value !== defaults.color, () => { color.value = defaults.color })}
            </div>
            <div class="sidebar-editor-swatches" role="radiogroup" aria-label="Color">
              {(Object.keys(NAV_COLORS) as NavColor[]).map(name => (
                <button
                  type="button"
                  key={name}
                  role="radio"
                  aria-checked={color.value === name}
                  aria-label={name === 'transparent' ? 'No background' : name}
                  title={name === 'transparent' ? 'No background' : name}
                  class={['sidebar-editor-swatch', { 'is-selected': color.value === name, 'is-transparent': name === 'transparent' }]}
                  style={{ backgroundColor: NAV_COLORS[name] }}
                  onClick={() => { color.value = name }}
                >
                  { color.value === name && <i class="pi pi-check" /> }
                </button>
              ))}
            </div>
          </div>

          <div class="sidebar-editor-field">
            <div class="sidebar-editor-label-row">
              <span class="sidebar-editor-label">Icon</span>
              {resetButton(icon.value !== defaults.icon, () => { icon.value = defaults.icon })}
            </div>
            <IconField class="tw:mb-2">
              <InputIcon class="pi pi-search" />
              <InputText v-model={iconFilter.value} placeholder="Filter icons" size="small" class="tw:w-full" aria-label="Filter icons" />
            </IconField>
            <div class="sidebar-editor-icons">
              {iconGroups.value.length === 0 && <p class="sidebar-picker-empty">No icons match.</p>}
              {iconGroups.value.map(group => (
                <section key={group.label}>
                  <h4 class="sidebar-picker-group-heading">{group.label}</h4>
                  <div class="sidebar-editor-icon-grid" role="radiogroup" aria-label={`${group.label} icons`}>
                    {group.icons.map(([name, IconComponent]) => (
                      <button
                        type="button"
                        key={name}
                        role="radio"
                        aria-checked={icon.value === name}
                        aria-label={name}
                        title={name}
                        class={['sidebar-editor-icon', { 'is-selected': icon.value === name }]}
                        onClick={() => { icon.value = name }}
                      >
                        {h(IconComponent, { class: 'tw:w-5 tw:h-5' })}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>

          <div class="sidebar-editor-footer">
            <Button type="button" size="small" label="Cancel" severity="secondary" outlined onClick={() => finish(null)} />
            <Button type="button" size="small" label="Save" onClick={save} />
          </div>
        </div>
      )
    }
  })

  const dialogRef = dialog.open(DialogContent, {
    props: {
      header: 'Edit sidebar item',
      style: { width: 'min(560px, calc(100vw - 2rem))' },
      pt: { root: 'sidebar-dialog-shell' },
      modal: true,
      closable: true,
      dismissableMask: false,
      closeOnEscape: true
    },
    onClose () {
      // The header close button and Escape both mean cancel.
      if (deferred.isPending) {
        deferred.resolve(null)
      }
    }
  })

  return deferred.promise
}

/** Prompts for a link's name, icon and color and applies the answer to the person's layout. */
export async function editSidebarLink (dialog: DialogServiceMethods, controller: SidebarLayoutController, link: ResolvedSidebarLink) {
  const overrides = await promptEditSidebarLink(dialog, { link })
  if (overrides) {
    controller.updateLink(link.id, overrides)
  }
}
